/**
 * onestop-page-api — the ONE seam between the Journey Designer and a One-Stop
 * page's real transport.
 *
 * WHY THIS FILE EXISTS, AND WHAT IT REPLACES
 * A One-Stop journey is not a `newjourneys` document. It is a `page_documents`
 * row with `doc_kind = 'experience'`, addressed by SLUG, with a draft/live split
 * — the same row the anonymous portal read (`getPortalPage`) serves to a student.
 * The designer's experience stub persisted through
 * `putExperience` → `PUT /v1/admin/newjourneys/experiences/{key}`, which is
 * key-addressed, writes no slug and has no draft/live split: nothing it stores
 * can ever reach the portal. It shares a `doc_kind` with the real resource and
 * nothing else. This module is the correct leg, and it is the only one a One-Stop
 * screen may call.
 *
 * WHAT IT IS NOT
 * No `fetch`, no route strings, no seed data. Every call goes through the typed
 * `@v0y4ge/api-client` ops.
 *
 * ⚠ THE DESIGN-TIME STUB, AND WHY IT IS HERE (2026-09-01)
 * `index.html` sets `const MOCK_FAMILIES = []` — MSW is switched OFF in the
 * preview harness, and has been since 2026-07-26 (the esbuild-wasm linker panics
 * on the full api-mocks graph). So NO mock route is served in preview, including
 * the authoring handlers written for this family: every one of the five ops 404s.
 *
 * That is also why every other journey type works. Queue, Appointment and Event
 * journeys never make an HTTP call at all — they persist through
 * `newjourneys-api.ts`, which is a documented localStorage envelope behind the
 * same kind of seam this module is. One-Stop was the only type wired to a real
 * op, which is the only reason it was the only one that failed.
 *
 * So this module now follows the same pattern, and for the same reason: ONE
 * module, ONE flag, every value marked. Flip `ONESTOP_STUBBED` to `false` the day
 * `MOCK_FAMILIES` is non-empty (or a worker is reachable) and the real ops take
 * over with no other change anywhere — which is what a seam is for.
 *
 * What the stub deliberately KEEPS honest:
 *   · the draft/live split, so publish still means something and a draft still
 *     changes nothing "public";
 *   · version numbering on publish;
 *   · `__source: "cd-local-stub"` on every record, so no stubbed row is ever
 *     mistaken for served data.
 * What it does NOT do: validate. Publish refusals come from the real validator,
 * so a stubbed publish always succeeds. The builder's readiness rows are the only
 * check in play at design time, which is exactly what they were built for.
 *
 * THE ONE ASYMMETRY WORTH REMEMBERING
 * `PUT` saves a draft and validates SHAPE only; `publish` validates MEANING and
 * refuses the whole document on any fault, writing nothing. So a half-finished
 * page saves cleanly and pointing at a queue that does not exist yet is legal
 * until the moment someone publishes.
 */
import {
  deletePortalExperiencePage,
  getPortalExperiencePage,
  listPortalExperiencePages,
  publishPortalExperiencePage,
  putPortalExperiencePage,
  resolvePortalHandoffDestinations, // LOCAL PATCH (platform side, 2026-09-15 preview hand-off destinations, JAI-1646)
} from "@v0y4ge/api-client";
import type {
  PortalExperienceBlock,
  PortalExperiencePagePutInput,
  PortalExperiencePageSummary,
  PortalExperiencePageView,
} from "@v0y4ge/api-contracts";
import type { Doc, Exp } from "./nj-model";
import { getActiveTenantSlug } from "../../shell/access";

export type OneStopDocument = PortalExperiencePagePutInput;
export type OneStopPage = PortalExperiencePageView;
export type OneStopBlockInstance = PortalExperienceBlock;

/* ── The design-time envelope ──────────────────────────────────────
 *  Per-tenant, shaped like the response the ops return, so the swap is a flag.
 */
/* FALSE, and it must stay false (re-applied 2026-09-08 after a pack reverted it).
   The five portal-experience ops are real and served. At `true` every One-Stop
   list/get/save/publish/delete answers from localStorage with a success shape and
   no request leaves the browser — the author is told their page published and
   nothing reached a student. */
const ONESTOP_STUBBED = false;
const STORE_KEY = (tenant: string) => `onestop.pages.${tenant || "default"}.v1`;

interface StubRecord {
  doc: OneStopDocument;
  published: boolean;
  liveVersion: number | null;
  updatedAt: string;
  publishedAt: string | null;
}

function readStore(tenant: string): Record<string, StubRecord> {
  try {
    const raw = localStorage.getItem(STORE_KEY(tenant));
    return raw ? (JSON.parse(raw) as Record<string, StubRecord>) : {};
  } catch {
    return {};
  }
}

function writeStore(tenant: string, next: Record<string, StubRecord>): void {
  try { localStorage.setItem(STORE_KEY(tenant), JSON.stringify(next)); }
  catch { /* quota — the in-memory copy the screen holds stays authoritative */ }
}

/** The tenant slug — the CONFIRMED active tenant from the shell.
 *  2026-09-04: this used to read `window.__cdTenantSlug` / `jrni_active_tenant`,
 *  which nothing sets, so every tenant shared one "default" store and Lakeview's
 *  designer opened Aperture's page. The shell's getter is the one source of the
 *  active tenant; the old keys stay as overrides for harness use. */
function tenantSlug(): string {
  try { return (window as any).__cdTenantSlug ?? localStorage.getItem("jrni_active_tenant") ?? getActiveTenantSlug() ?? "default"; }
  catch { return "default"; }
}

function stubView(slug: string, record: StubRecord): OneStopPage {
  return {
    slug,
    title: record.doc.title,
    header: record.doc.header,
    blocks: record.doc.blocks,
    settings: record.doc.settings,
    published: record.published,
    liveVersion: record.liveVersion,
    updatedAt: record.updatedAt,
    publishedAt: record.publishedAt,
    /* Marked, per the seeded-op convention: a screen can say where a row came
       from, and no stubbed row is ever mistaken for served data. */
    __source: "cd-local-stub",
  } as unknown as OneStopPage;
}

/* ── The five ops ──────────────────────────────────────────────────────
 *  Thin by design: a wrapper that adds behaviour is a second implementation of
 *  the contract. The only thing added is the unwrap of `{ data }`, so a screen
 *  never handles an envelope.
 */

/** The op's own cap. Asking for more is refused, not clamped. */
const LIST_LIMIT = 200;

export async function listOneStopPages(): Promise<PortalExperiencePageSummary[]> {
  if (ONESTOP_STUBBED) {
    const tenant = tenantSlug();
    return Object.entries(readStore(tenant)).map(([slug, record]) => ({
      slug,
      title: record.doc.title,
      navLabel: record.doc.settings.navLabel,
      published: record.published,
      updatedAt: record.updatedAt,
      publishedAt: record.publishedAt,
    })) as PortalExperiencePageSummary[];
  }
  /* EVERY page, not page 1 (2026-09-09, on the API line's correction).
   *
   * The op DOES page — `{ page?, limit? }` in, `pagination.total_pages` back,
   * limit capped at 200 — and calling it bare returns the server's default
   * first page. Every caller here wants the whole set: the root resolver picks
   * the first nav-listed slug, and the attach path de-dupes against existing
   * slugs. Both are wrong on a truncated list, and wrong QUIETLY — a slug on
   * page 2 reads as absent, so the de-dupe mints it again and the upserting PUT
   * overwrites a page while reporting "created".
   *
   * `total_pages` is read from the first response rather than looped until a
   * short page: a short page is not proof of the end, and this way the number
   * of requests is known after one. */
  const first = await listPortalExperiencePages({ page: 1, limit: LIST_LIMIT });
  const pages = [...first.data];
  const total = first.pagination?.total_pages ?? 1;
  for (let p = 2; p <= total; p++) {
    const next = await listPortalExperiencePages({ page: p, limit: LIST_LIMIT });
    pages.push(...next.data);
  }
  return pages;
}

/**
 * WHICH DOCUMENT IS THE PORTAL'S ROOT (2026-09-05).
 *
 * There is no `onestop` document. On the demo tenant the portal's experience
 * documents are `home`, `beratung`, `wartezeiten`, and a tenant names them in its
 * own language — so the root can only be RESOLVED, never spelled. The rule, in
 * order, and each step is something the list response actually carries:
 *
 *   1. the first page that opted into the nav (`navLabel` non-null) — the nav's
 *      first entry is the portal's root by construction;
 *   2. failing that, the first PUBLISHED page — a tenant that publishes one page
 *      and hides it from its own nav still has a root;
 *   3. failing that, the first page at all (a draft-only tenant, mid-setup);
 *   4. `null` — no experience documents exist, which is a state the editor has to
 *      show rather than paper over with an invented slug.
 */
export async function resolvePortalRootSlug(): Promise<string | null> {
  const pages = await listOneStopPages();
  if (pages.length === 0) return null;
  const navListed = pages.find((p) => p.navLabel !== null && p.navLabel !== "");
  if (navListed) return navListed.slug;
  const published = pages.find((p) => p.published);
  return (published ?? pages[0]!).slug;
}

/**
 * The Event List tab's document, created on first save (2026-09-05).
 *
 * There is no `events` document on the demo tenant, and the tab has to have one
 * to write into. Rather than a new record type, first save CREATES the experience
 * document through the operation that already exists — `saveOneStopDraft` (PUT by
 * slug, which creates when the slug is free) — carrying ONE `events` block with
 * the contract's defaults. Publishing stays a separate act, as everywhere else.
 *
 * Idempotent: an existing document is returned untouched, so a second save never
 * overwrites an author's blocks with the seed.
 */
export async function ensureEventsDocument(args: {
  slug: string;
  title: string;
  timeZone: string;
  locale?: string;
  navLabel?: string | null;
}): Promise<{ page: OneStopPage; created: boolean }> {
  try {
    const existing = await getOneStopPage(args.slug);
    return { page: existing, created: false };
  } catch (err) {
    if ((err as { status?: number } | null)?.status !== 404) throw err;
  }
  const doc = emptyOneStopDocument({
    title: args.title,
    timeZone: args.timeZone,
    locale: args.locale,
    navLabel: args.navLabel ?? args.title,
  });
  doc.blocks = [{
    id: "events",
    type: "events",
    version: 1,
    config: { maxItems: 6, showAllEventsLink: true },
  } as OneStopBlockInstance];
  const page = await saveOneStopDraft(args.slug, doc);
  return { page, created: true };
}

export async function getOneStopPage(slug: string): Promise<OneStopPage> {
  if (ONESTOP_STUBBED) {
    const record = readStore(tenantSlug())[slug];
    if (!record) throw Object.assign(new Error(`No One-Stop page "${slug}".`), { status: 404 });
    return stubView(slug, record);
  }
  const res = await getPortalExperiencePage(slug);
  return res.data;
}

/** Saves the DRAFT. Nothing a student sees changes. Idempotent. */
export async function saveOneStopDraft(slug: string, doc: OneStopDocument): Promise<OneStopPage> {
  if (ONESTOP_STUBBED) {
    const tenant = tenantSlug();
    const store = readStore(tenant);
    const prev = store[slug];
    const next: StubRecord = {
      doc,
      /* A draft save leaves the live version exactly where it was — the whole
         point of the split, and the thing a naive stub would get wrong. */
      published: prev?.published ?? false,
      liveVersion: prev?.liveVersion ?? null,
      updatedAt: new Date().toISOString(),
      publishedAt: prev?.publishedAt ?? null,
    };
    store[slug] = next;
    writeStore(tenant, store);
    return stubView(slug, next);
  }
  const res = await putPortalExperiencePage(slug, doc);
  return res.data;
}

/** Promotes draft → live. All-or-nothing; a refusal writes nothing. */
export async function publishOneStopPage(slug: string, notes?: string): Promise<OneStopPage> {
  if (ONESTOP_STUBBED) {
    const tenant = tenantSlug();
    const store = readStore(tenant);
    const prev = store[slug];
    if (!prev) throw Object.assign(new Error(`No One-Stop page "${slug}".`), { status: 404 });
    /* No validation here, deliberately: refusals are the real validator's, and a
       stub that invented its own would teach an author rules the server does not
       have. The builder's readiness rows are the design-time check. */
    const next: StubRecord = {
      ...prev,
      published: true,
      liveVersion: (prev.liveVersion ?? 0) + 1,
      updatedAt: new Date().toISOString(),
      publishedAt: new Date().toISOString(),
    };
    store[slug] = next;
    writeStore(tenant, store);
    return stubView(slug, next);
  }
  const res = await publishPortalExperiencePage(slug, notes ? { notes } : {});
  return res.data;
}

export async function deleteOneStopPage(slug: string): Promise<void> {
  if (ONESTOP_STUBBED) {
    const tenant = tenantSlug();
    const store = readStore(tenant);
    delete store[slug];
    writeStore(tenant, store);
    return;
  }
  await deletePortalExperiencePage(slug);
}

/* ── Slug ──────────────────────────────────────────────────────────────
 *  The slug is the page's URL and its identity: `PUT` to a different slug
 *  creates a different page rather than renaming one, and the collection
 *  refuses a slug another page already holds (409). So it is minted once, from
 *  the title the author first typed, and never re-derived when they rename the
 *  page afterwards.
 */
export function slugFromTitle(title: string): string {
  /* NFKD splits an accented letter into letter + combining mark, and the
     non-alphanumeric replace below removes the mark on the same pass — so there
     is deliberately no `\u0300-\u036f` character class here. That construct does
     not survive this project's upload channel intact (it is decoded in transit
     as a JSON escape, per the kit's §6), and here it would be redundant. */
  const slug = title
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120)
    .replace(/-+$/g, "");
  return slug || "one-stop";
}

/**
 * A slug nothing else has claimed.
 *
 * The collection refuses a duplicate with a 409, and the slug is minted from the
 * title — so two pages both first called "One-Stop" would collide on the second
 * save. Suffixing is done here, once, rather than by catching the 409: a 409 has
 * already told the author their page failed to save, and there is nothing for
 * them to decide.
 */
export function uniqueSlug(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; n < 500; n += 1) {
    /* Room for the suffix: the contract caps a slug at 120 characters. */
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, 120 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${base.slice(0, 106)}-${Date.now().toString(36)}`;
}

/**
 * A page with no blocks yet.
 *
 * `timeZone` is a REQUIRED argument and has no default on purpose. Every civil
 * date and opening time on the page is read in this zone, so a guessed value is
 * a published page that tells students the wrong hour. The caller reads the
 * tenant's zone; if it cannot, it must ask rather than let this module invent
 * one. Same reasoning, less severe, for `locale`.
 */
export function emptyOneStopDocument(args: {
  title: string;
  timeZone: string;
  locale?: string;
  supportedLocales?: string[];
  navLabel?: string | null;
  subtitle?: string | null;
}): OneStopDocument {
  const locale = args.locale ?? "en-US";
  /* The contract refuses a default locale outside the supported set, so the
     default is always a member — including when a caller passes a list that
     omits it. */
  const supported = args.supportedLocales?.length ? args.supportedLocales : [locale];
  return {
    title: args.title,
    header: { eyebrow: null, title: args.title, subtitle: args.subtitle ?? null },
    blocks: [],
    settings: {
      navLabel: args.navLabel ?? null,
      locale,
      supportedLocales: supported.includes(locale) ? supported : [locale, ...supported],
      timeZone: args.timeZone,
      /* layout / showHeader / showFooter are required on the settings object
         (JAI-1473) and were omitted here. Contract defaults, spelled out rather
         than left to the schema: this object is typed as the OUTPUT shape, where
         a defaulted field is present, so omitting it is a type error and not a
         "let the server decide" (2026-09-08). */
      layout: "get-help-first",
      showHeader: true,
      showFooter: true,
    },
  };
}

/* ── Block ids ─────────────────────────────────────────────────────────
 *  Author-chosen, kebab-case, unique within the page, and stable across
 *  reordering. Not cosmetic: the id is the `path` in every publish refusal
 *  (`blocks.move-in-banner.activeFrom`), the key in the audit event's change
 *  entries, and what the resolver names in a skip note. A uuid makes all three
 *  unreadable to whoever has to act on them, and a position-derived id
 *  (`block-3`) breaks the moment a block moves — which is the primary
 *  interaction on this canvas.
 */
export function mintBlockId(type: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(type)) return type;
  for (let n = 2; n < 500; n += 1) {
    const candidate = `${type}-${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${type}-${Date.now()}`;
}

/* ── Publish refusals ──────────────────────────────────────────────────
 *  A 422 `DOCUMENT_PUBLISH_INVALID` names each fault as
 *  `fields[].path = blocks.<blockId>[.<configPath>]`. That shape exists so the
 *  editor can mark the offending BLOCK, which is why this returns a map keyed by
 *  block id rather than a joined sentence for a toast.
 *
 *  The reader is defensive about where `fields` sits because the envelope is
 *  unwrapped differently depending on how far up the error travelled, and a
 *  refusal that renders as "something went wrong" is the one outcome worth
 *  ruling out.
 */
export interface PublishFault {
  /** The config path under the block, or null when the fault is the block itself. */
  readonly path: string | null;
  readonly message: string;
}

function faultFields(err: unknown): { path?: unknown; message?: unknown }[] {
  const bag = err as Record<string, any> | null;
  if (!bag || typeof bag !== "object") return [];
  const candidates = [bag.fields, bag.error?.fields, bag.details?.fields, bag.body?.error?.fields];
  const found = candidates.find((value) => Array.isArray(value));
  return Array.isArray(found) ? found : [];
}

export function publishFaultsByBlock(err: unknown): Map<string, PublishFault[]> {
  const byBlock = new Map<string, PublishFault[]>();
  for (const field of faultFields(err)) {
    const path = typeof field.path === "string" ? field.path : "";
    const message = typeof field.message === "string" ? field.message : "";
    if (!path.startsWith("blocks.") || !message) continue;
    /* `blocks.<id>` or `blocks.<id>.<rest>` — the id never contains a dot
       (kebab-case is enforced by the contract), so one split is exact. */
    const rest = path.slice("blocks.".length);
    const dot = rest.indexOf(".");
    const id = dot === -1 ? rest : rest.slice(0, dot);
    const configPath = dot === -1 ? null : rest.slice(dot + 1);
    if (!id) continue;
    const list = byBlock.get(id) ?? [];
    list.push({ path: configPath, message });
    byBlock.set(id, list);
  }
  return byBlock;
}

/** Faults that name no block — the document as a whole. */
export function publishFaultsForDocument(err: unknown): PublishFault[] {
  return faultFields(err)
    .filter((f) => typeof f.path === "string" && !f.path.startsWith("blocks."))
    .map((f) => ({ path: typeof f.path === "string" ? f.path : null, message: String(f.message ?? "") }))
    .filter((f) => f.message.length > 0);
}

/* ── Migrating an anchor-and-attach experience ─────────────────────────
 *  The old model held an anchor journey plus a list of attached journeys. Under
 *  the fold, attachment IS scope: a reference in a block's config instead of a
 *  row in `attached[]`. Both hold an id and neither holds a copy, so ruling ④
 *  survives — the resolver still fetches the real object at read time.
 *
 *  Only three mappings are certain, and this function makes only those:
 *
 *    Event journey       → `register-event` { eventId }
 *    Queue journey       → `join-queue`     { queueId }
 *    Appointment journey → `appointments`   { scope: { services: […] } }
 *
 *  THE APPOINTMENT CASE IS PLURAL, AND THAT IS THE PLATFORM'S SHAPE, NOT A GAP.
 *  A queue journey references one queue and an event journey one event, so both
 *  map to a single-id flow-entry block. An appointment journey's owner is a
 *  CATALOGUE GROUP holding N bookable services — so its honest counterpart is the
 *  plural `appointments` block scoped to those services, not a flow-entry card
 *  pointing at one of them. `origin.id` is the group id and no block config takes
 *  one; the group's MEMBERS are what the vocabulary takes, and they are already
 *  on the stored document — the `apptsvc` page's `svc`, seeded from
 *  `g.serviceIds`. So the migration reads those.
 *
 *  What it cannot preserve: `scope.services` is a SNAPSHOT of the group as it
 *  stood. A service added to the catalogue group next week will not appear on the
 *  page, where a reference to the group itself would have followed it. Making it
 *  live needs a vocabulary the registry does not have — a `scope.groups`, or a
 *  `book-appointment.preselects.catalogueGroupId` — and that is a contract ask,
 *  not something this file can decide.
 *
 *  A journey never attached to a real object (`origin.kind === "local"`) has no
 *  id to reference at all, and a nested experience has no v1 equivalent. Both
 *  are returned in `skipped` and NOT silently dropped: the caller shows them,
 *  because a migration that quietly loses a row is worse than one that says what
 *  it could not carry. A group with no services yet is the milder case — the
 *  block is created and reported in `needsScope` for the author to fill in.
 */
export interface ExpMigration {
  readonly blocks: OneStopBlockInstance[];
  /** Block ids whose scope the author still has to fill in. */
  readonly needsScope: string[];
  /** Rows that produced no block, with the reason. */
  readonly skipped: { docKey: string; name: string; reason: string }[];
}

export function expToOneStopBlocks(exp: Exp, docs: Record<string, Doc | undefined>): ExpMigration {
  const blocks: OneStopBlockInstance[] = [];
  const needsScope: string[] = [];
  const skipped: { docKey: string; name: string; reason: string }[] = [];
  const ids: string[] = [];

  const add = (type: string, config: Record<string, unknown>): string => {
    const id = mintBlockId(type, ids);
    ids.push(id);
    blocks.push({ id, type, version: 1, config } as OneStopBlockInstance);
    return id;
  };

  /* The anchor first: it was the top of the old hub, so it is the first block
     of the new page. Order is the page order and nothing reorders it later. */
  const rows = [
    ...(exp.anchor ? [exp.anchor] : []),
    ...exp.attached.map((row) => row.doc),
  ];

  for (const docKey of rows) {
    const doc = docs[docKey];
    const name = doc?.name ?? docKey;
    const origin = doc?.origin;
    if (!doc) {
      skipped.push({ docKey, name, reason: "the journey it referenced no longer exists" });
      continue;
    }
    if (!origin || origin.kind === "local") {
      skipped.push({ docKey, name, reason: "never attached to a real queue, service or event — nothing to reference" });
      continue;
    }
    if (origin.kind === "event") {
      add("register-event", { eventId: origin.id });
      continue;
    }
    if (origin.kind === "queue") {
      add("join-queue", { queueId: origin.id });
      continue;
    }
    if (origin.kind === "appointment") {
      /* The group's member services, off the page the catalogue seeded. Deduped
         and blank-filtered: an author-edited page may carry an empty row, and a
         blank id publishes as a dangling reference. */
      const services = [
        ...new Set(
          doc.pages
            .filter((page) => page.id === "apptsvc")
            .flatMap((page) => (Array.isArray(page.svc) ? page.svc : []))
            .map((serviceId) => String(serviceId).trim())
            .filter((serviceId) => serviceId.length > 0),
        ),
      ];
      const id = add("appointments", { scope: services.length ? { services } : {} });
      if (services.length === 0) needsScope.push(id);
      continue;
    }
    skipped.push({ docKey, name, reason: `a ${origin.kind} journey has no One-Stop block in v1` });
  }

  return { blocks, needsScope, skipped };
}

/** The whole document a migrated experience becomes, blocks included. */
export function expToOneStopDocument(
  exp: Exp,
  docs: Record<string, Doc | undefined>,
  timeZone: string,
): { doc: OneStopDocument; slug: string; migration: ExpMigration } {
  const title = exp.name?.trim() || "One-Stop";
  const migration = expToOneStopBlocks(exp, docs);
  const base = emptyOneStopDocument({ title, timeZone });
  return {
    slug: slugFromTitle(title),
    doc: { ...base, blocks: migration.blocks },
    migration,
  };
}

/* LOCAL PATCH (platform side, 2026-09-15 preview hand-off destinations, JAI-1646).
   The three URLs a live card hands off to, by id, from the server that composes
   them for the page. The builder holds a DRAFT and cannot compose these itself:
   each is a join of the tenant's public host for the surface, the ONE live
   appointment journey's slug and the event's public route slug — facts only the
   servers hold, and the exact logic JAI-1629 moved OUT of the browser. An id the
   page would carry no destination for answers null: the queue and event
   renderers then show their inline flow, and the appointment renderer falls
   through to the composed slug hand-off it always had — the same as the live
   page for the same null. Under the stub there is no server, so the answer is
   empty maps and the preview keeps what it always showed. The server bounds
   each list to 50 ids, so each is cut to its first 50 here rather than letting
   one over-long list refuse all three. */
export type OneStopHandoffDestinations = { queues: Record<string, string | null>; services: Record<string, string | null>; events: Record<string, string | null> };
export async function resolveOneStopHandoffDestinations(ids: { queues: string[]; services: string[]; events: string[] }): Promise<OneStopHandoffDestinations> {
  if (ONESTOP_STUBBED || (ids.queues.length === 0 && ids.services.length === 0 && ids.events.length === 0)) return { queues: {}, services: {}, events: {} };
  const bounded = { queues: ids.queues.slice(0, 50), services: ids.services.slice(0, 50), events: ids.events.slice(0, 50) };
  const res = await resolvePortalHandoffDestinations(bounded);
  return res.data;
}
